import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Save, Loader2 } from "lucide-react";

interface FormActionBarProps {
  hasChanges: boolean;
  hasValidChanges: boolean;
  isSubmitting: boolean;
  mode: "create" | "edit";
  onSubmit: () => void;
  onCancel: () => void;
  className?: string;
  attachmentsSlot?: React.ReactNode;
}

export default function FormActionBar({
                                        hasChanges,
                                        hasValidChanges,
                                        isSubmitting,
                                        mode,
                                        onSubmit,
                                        onCancel,
                                        className,
                                        attachmentsSlot,
                                      }: FormActionBarProps) {
  // Track whether form has been successfully saved at least once
  const [savedOnce, setSavedOnce] = useState(false);

  // When isSubmitting transitions from true → false and no changes remain,
  // a save just completed successfully
  const [wasSubmitting, setWasSubmitting] = useState(false);

  useEffect(() => {
    if (wasSubmitting && !isSubmitting && !hasChanges) {
      setSavedOnce(true);
    }
    setWasSubmitting(isSubmitting);
  }, [isSubmitting, hasChanges]);

  // Reset savedOnce when new changes are made
  useEffect(() => {
    if (hasChanges) setSavedOnce(false);
  }, [hasChanges]);

  const showSaved     = savedOnce && !hasChanges && !isSubmitting;
  const showUnsaved   = hasChanges && !isSubmitting;
  const showSaving    = isSubmitting;

  return (
      <div className={cn(
          "prescription-action-bar shadow-[0_-2px_12px_rgba(0,0,0,0.06)]",
          className
      )}>
        <div className="px-4 py-2">
          <div className="flex items-center justify-between gap-4">

            {/* Status indicator — inline, no box */}
            <div className="flex items-center gap-2 min-w-0 flex-1 h-8">
              {showSaving && (
                  <div className="flex items-center gap-1.5 text-xs text-primary">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Saving…</span>
                  </div>
              )}
              {showUnsaved && (
                  <div className="flex items-center gap-1.5 text-xs text-warning">
                    <div className="w-1.5 h-1.5 bg-warning rounded-full animate-pulse flex-shrink-0" />
                    <span>Unsaved changes</span>
                  </div>
              )}
              {showSaved && (
                  <div className="flex items-center gap-1.5 text-xs text-success">
                    <div className="w-1.5 h-1.5 bg-success rounded-full flex-shrink-0" />
                    <span>Saved</span>
                  </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {attachmentsSlot}

              <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onCancel}
                  disabled={isSubmitting}
                  className="h-8 px-3 text-xs text-muted-foreground"
              >
                Cancel
              </Button>

              <Button
                  type="submit"
                  size="sm"
                  onClick={onSubmit}
                  disabled={!hasValidChanges || isSubmitting}
                  className="h-8 px-4 text-xs gap-1.5"
              >
                {isSubmitting ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                    <>
                      <Save className="w-3.5 h-3.5" />
                      {mode === "edit" ? "Update" : "Save"} Prescription
                    </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
  );
}